import { useState, useEffect, useMemo } from "react";
import {
  addDays, clampDateToMidnight, startOfWeekMonday, isSameDay, minutesToDate
} from "../../admin/scheduling/dateUtils";
import { dateKeyLocal } from "../../admin/scheduling/dayConfigUtils";
import {
  fetchAvailabilityBlocks, fetchDayConfigsForDates, fetchTutorClockIntervals, SESSIONS_COL
} from "../../admin/scheduling/schedulingApi";
import { fetchMyTutorDoc } from "../tutorFirestore";
import { db } from "../../../firebaseConfig";
import { collection, query, where, onSnapshot, Timestamp } from "firebase/firestore";
import { DAYS_ORDER, WEEKDAY_BLOCKS, SATURDAY_BLOCKS } from "../../admin/scheduling/constants";
import {
  dayKeyFromDate,
  defaultTimelineRangeForDayKey as defaultScheduleTimelineRangeForDayKey,
  resolveScheduleForDate,
} from "../../admin/scheduling/scheduleSettingsUtils";

// Helpers
function defaultBlocksForDayKey(dayKey) { return dayKey === "saturday" ? SATURDAY_BLOCKS : WEEKDAY_BLOCKS; }
function defaultTimelineRangeForDayKey(dayKey) {
  return defaultScheduleTimelineRangeForDayKey(dayKey);
}
function resolveTimelineRangeForDay(dayKey, config = {}) {
  const defaults = defaultTimelineRangeForDayKey(dayKey);
  const rawStart = Number(config?.timelineStartMin);
  const rawEnd = Number(config?.timelineEndMin);
  if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd) || rawEnd <= rawStart) {
    return defaults;
  }
  const startMin = Math.max(0, Math.min(23 * 60 + 55, rawStart));
  const endMin = Math.max(startMin + 60, Math.min(24 * 60, rawEnd));
  return { startMin, endMin };
}
function getDayKeyForDate(d) {
  return dayKeyFromDate(d);
}
function overlaps(aStart, aEnd, bStart, bEnd) { return aStart < bEnd && bStart < aEnd; }
function minuteOfDay(value) {
  const d = value instanceof Date ? value : new Date(value);
  return d.getHours() * 60 + d.getMinutes();
}

function shouldShowClockIndicatorsForTutor(tutor) {
  const role = String(tutor?.role || "").toLowerCase();
  return role !== "admin";
}

export function useTutorScheduleModel(currentUserUid) {
  const today = useMemo(() => clampDateToMidnight(new Date()), []);
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(today));
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  
  const [myTutorProfile, setMyTutorProfile] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clockIntervals, setClockIntervals] = useState([]);
  const [dayConfigs, setDayConfigs] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  // 1. Load Profile
  useEffect(() => {
    if (!currentUserUid) return;
    let alive = true;
    fetchMyTutorDoc(currentUserUid).then(res => {
      if (alive && res) setMyTutorProfile(res);
    });
    return () => { alive = false; };
  }, [currentUserUid]);

  // 2a. Load availability + clocks (regular fetch)
  useEffect(() => {
    if (!currentUserUid) return;
    let alive = true;
    (async () => {
      const rangeStart = addDays(weekStart, -2);
      const rangeEnd = addDays(weekEnd, 3);
      const [avail, clocks] = await Promise.all([
        fetchAvailabilityBlocks({ rangeStart, rangeEnd, tutorUid: currentUserUid }),
        fetchTutorClockIntervals({ rangeStart, rangeEnd, tutorUid: currentUserUid }),
      ]);
      if (!alive) return;
      setAvailability(avail);
      setClockIntervals(clocks);
    })();
    return () => { alive = false; };
  }, [weekStart, weekEnd, currentUserUid, refreshKey]);

  // 2b. Sessions — real-time via Firestore onSnapshot
  useEffect(() => {
    if (!currentUserUid) return;
    const rangeStart = addDays(weekStart, -2);
    const rangeEnd = addDays(weekEnd, 3);
    const startTs = Timestamp.fromDate(rangeStart);
    const endTs = Timestamp.fromDate(rangeEnd);

    const q = query(
      collection(db, SESSIONS_COL),
      where("start", ">=", startTs),
      where("start", "<", endTs),
      where("tutorUid", "==", currentUserUid)
    );

    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => {
        const data = d.data();
        const toDate = (ts) => {
          if (!ts) return null;
          if (ts instanceof Date) return ts;
          if (typeof ts.toDate === "function") return ts.toDate();
          return null;
        };
        return { id: d.id, ...data, start: toDate(data.start), end: toDate(data.end) };
      }).filter((s) => !s?.isDeleted);
      setSessions(rows);
    }, (err) => {
      console.error("Tutor sessions real-time listener error:", err);
    });

    return () => unsub();
  }, [weekStart, weekEnd, currentUserUid]);

  // 3. Load Day Configs
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i <= 6; i++) {
      const d = addDays(weekStart, i);
      const dayKey = getDayKeyForDate(d);
      if (dayKey) days.push({ dayKey, date: d });
    }
    return days.filter((x) => DAYS_ORDER.includes(x.dayKey));
  }, [weekStart]);

  useEffect(() => {
    const dates = [];
    for (let offset = -45; offset <= 75; offset += 1) {
      dates.push(addDays(weekStart, offset));
    }
    fetchDayConfigsForDates(dates).then(setDayConfigs);
  }, [weekStart, refreshKey]);

  // 4. Build Model
  const model = useMemo(() => {
    const result = {};
    for (const { dayKey, date } of weekDays) {
      const dk = dateKeyLocal(date);
      const cfg = dayConfigs[dk];
      const resolvedDay = resolveScheduleForDate(date, cfg);
      const timeBlocks = cfg?.timeBlocks?.length ? cfg.timeBlocks : defaultBlocksForDayKey(dayKey);
      const isClosed = !!resolvedDay.isClosed;
      const { startMin: timelineStartMin, endMin: timelineEndMin } = resolveTimelineRangeForDay(dayKey, {
        timelineStartMin: resolvedDay.startMin,
        timelineEndMin: resolvedDay.endMin,
      });
      const tutorRows = [];
      
      if (myTutorProfile) {
        const tutor = myTutorProfile; 
        const availForTutorDay = availability.filter(
          (a) => a.uid === tutor.id && isSameDay(a.start, date)
        );
        const visibleAvailability = availForTutorDay.filter((a) => !a.unavailable);
        const dayTimelineStart = minutesToDate(date, timelineStartMin);
        const dayTimelineEnd = minutesToDate(date, timelineEndMin);
        const hasTimelineAvailability = visibleAvailability.some((a) =>
          overlaps(a.start, a.end, dayTimelineStart, dayTimelineEnd)
        );
        const sessForTutorDay = sessions.filter((s) => {
          if (s.tutorUid !== tutor.id) return false;
          if (!isSameDay(s.start, date)) return false;
          return true;
        });

        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = addDays(dayStart, 1);
        const clockForTutorDay = shouldShowClockIndicatorsForTutor(tutor)
          ? (clockIntervals || [])
            .filter((interval) => interval.uid === tutor.id && interval.start < dayEnd && interval.end > dayStart)
            .map((interval) => {
              const clippedStart = interval.start < dayStart ? dayStart : interval.start;
              const clippedEnd = interval.end > dayEnd ? dayEnd : interval.end;
              const startMin = minuteOfDay(clippedStart);
              const endMin = minuteOfDay(clippedEnd);
              if (endMin <= startMin) return null;
              return {
                ...interval,
                start: clippedStart,
                end: clippedEnd,
                startMin,
                endMin,
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.startMin - b.startMin)
          : [];

        const blocksMap = {};

        for (const block of timeBlocks) {
          const blockStart = minutesToDate(date, block.startMin);
          const blockEnd = minutesToDate(date, block.endMin);
          const isAvail = visibleAvailability.some((a) => overlaps(a.start, a.end, blockStart, blockEnd));

          const seatArr = [[], [], []]; 
          const sessForCell = sessForTutorDay.filter((s) => (s.blockId || "") === block.id);

          for (const s of sessForCell) {
            const idx = s.slotIdx ?? 0;
            if (seatArr[idx]) {
                const durationMin = Math.round((s.end - s.start) / 60000);
                const blockDurationMin = block.endMin - block.startMin;
                
                // ✅ FIX: Construct proper Label (Name + Grade)
                const studentLabel = (s.studentName && s.studentGrade != null) 
                   ? `${s.studentName} (${s.studentGrade})`
                   : (s.studentName || "Student");

                seatArr[idx].push({ 
                  ...s, 
                  durationMin, 
                  blockDurationMin,
                  studentLabel // Pass this to the UI
                });
            }
          }
          blocksMap[block.id] = { isAvailable: isAvail, sessionLists: seatArr };
        }

        const hasClockIntervals = clockForTutorDay.length > 0;
        if (hasTimelineAvailability || sessForTutorDay.length > 0 || hasClockIntervals) {
          tutorRows.push({ 
            tutor, 
            blocks: blocksMap,
            availabilities: visibleAvailability,
            clockIntervals: clockForTutorDay, 
          }); 
        }
      }
      result[dayKey] = { date, timeBlocks, tutorRows, isClosed, timelineStartMin, timelineEndMin };
    }
    return result;
  }, [weekDays, myTutorProfile, availability, sessions, dayConfigs, clockIntervals]);

  const refresh = () => setRefreshKey(prev => prev + 1);

  return {
    today,
    weekStart,
    setWeekStart,
    weekEnd,
    weekDays,
    model,
    dayConfigs,
    refresh,
    refreshKey
  };
}
